import type { BookingRulesEntry, BookingRulesSettings, UpdateBookingRulesRequest } from "@planerius/contracts/cabinet"

import { minutesLabel } from "../../lib/plural"

/**
 * 4-Б4 «Правила записи» как данные, отдельно от разметки.
 *
 * СЕМЬ НАСТРОЕК, И КАЖДАЯ ПОДПИСАНА ПОСЛЕДСТВИЕМ, А НЕ НАЗВАНИЕМ ПОЛЯ. «Минимальный лаг» —
 * это «чтобы мастер успел увидеть запись»; ноль здесь означает клиента, записавшегося
 * на время через пять минут. Тексты — из листа «Настройки и каналы — П1» (4-Б4).
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Политика «Только с предоплатой» показана, но выбрать её нельзя:
 * платформа не проводит расчёты салона с его клиентами, и потребовать предоплату нечем
 * (решение по вопросу 26 за владельцем). Обещать её работающей — значит продать салону
 * защиту, которой нет.
 */

export type RuleField =
  | "minLeadMinutes"
  | "maxHorizonDays"
  | "cancelDeadlineMinutes"
  | "rescheduleDeadlineMinutes"
  | "quarantineMinutes"
  | "maxActiveAppointments"
  | "holdTtlSeconds"

export interface RuleRow {
  readonly field: RuleField
  readonly name: string
  readonly hint: string
  readonly options: readonly { readonly value: number; readonly label: string }[]
}

const MINUTES = (values: readonly number[]): { value: number; label: string }[] =>
  values.map((value) => ({ value, label: value === 0 ? "сразу, без запаса" : minutesLabel(value) }))

const DAYS = (values: readonly number[]): { value: number; label: string }[] =>
  values.map((value) => ({ value, label: `${String(value)} ${value % 10 === 1 && value % 100 !== 11 ? "день" : value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 10 || value % 100 >= 20) ? "дня" : "дней"}` }))

export const RULE_ROWS: readonly RuleRow[] = [
  {
    field: "minLeadMinutes",
    name: "Записываться не позднее чем за…",
    hint: "Чтобы мастер успел увидеть запись. Если поставить 0, клиент запишется на время через пять минут.",
    options: MINUTES([0, 15, 30, 60, 120, 180, 360, 720, 1440]),
  },
  {
    field: "maxHorizonDays",
    name: "Записываться не раньше чем за…",
    hint: "Дальше этого срока клиент не увидит свободного времени.",
    options: DAYS([7, 14, 30, 60, 90, 180, 365]),
  },
  {
    field: "cancelDeadlineMinutes",
    name: "Клиент может отменить сам не позднее чем за…",
    hint: "Позже — только через администратора.",
    options: MINUTES([0, 30, 60, 120, 180, 360, 720, 1440]),
  },
  {
    field: "rescheduleDeadlineMinutes",
    name: "Клиент может перенести сам не позднее чем за…",
    hint: "Отдельно от отмены: это разные решения салона.",
    options: MINUTES([0, 30, 60, 120, 180, 360, 720, 1440]),
  },
  {
    field: "quarantineMinutes",
    name: "Освободившееся время не возвращать в онлайн-запись…",
    hint: "Чтобы администратор успел предложить его листу ожидания.",
    options: MINUTES([0, 15, 30, 60, 120]),
  },
  {
    field: "maxActiveAppointments",
    name: "Одновременно активных записей у клиента не больше…",
    hint: "Защита от брони всего дня на всякий случай.",
    options: [1, 2, 3, 5, 10].map((value) => ({ value, label: String(value) })),
  },
  {
    field: "holdTtlSeconds",
    name: "Держим время, пока клиент оформляет запись…",
    hint: "Чтобы слот не увели, пока он вводит код из СМС.",
    options: [60, 120, 300, 600, 900].map((value) => ({ value, label: minutesLabel(value / 60) })),
  },
]

export type NoShowPolicy = BookingRulesEntry["noShowPolicy"]

export interface PolicyOption {
  readonly value: NoShowPolicy
  readonly label: string
  /** Почему выбрать нельзя. null — можно. */
  readonly deniedReason: string | null
}

export const POLICY_OPTIONS: readonly PolicyOption[] = [
  { value: "none", label: "Только считать", deniedReason: null },
  { value: "warn", label: "Предупреждать администратора", deniedReason: null },
  {
    value: "require_prepay",
    label: "Только с предоплатой",
    deniedReason:
      "Платформа не проводит расчёты салона с клиентами, поэтому потребовать предоплату нечем. Решение по этому варианту — за владельцем.",
  },
  { value: "admin_only", label: "Только через администратора", deniedReason: null },
  { value: "block", label: "Запретить запись", deniedReason: null },
]

export const TEXT_BLACKLIST_SCOPE =
  "Чёрный список действует только в вашем салоне. Общего списка по платформе нет и не будет — ваши клиенты остаются вашими."

/**
 * Предупреждение из макета: отменить станет нельзя раньше, чем человек успеет записаться.
 * Это не запрет — салон вправе так работать, — но сказать об этом надо до сохранения.
 */
export function deadlineWarning(rules: Pick<BookingRulesEntry, "cancelDeadlineMinutes" | "minLeadMinutes">): string | null {
  if (rules.cancelDeadlineMinutes <= rules.minLeadMinutes) return null
  return `Срок отмены ${minutesLabel(rules.cancelDeadlineMinutes)} больше, чем срок записи ${minutesLabel(rules.minLeadMinutes)}. Получится, что клиент запишется за час, а отменить уже не сможет. Проверьте оба значения.`
}

const COMPARED: readonly (keyof BookingRulesEntry)[] = [
  "minLeadMinutes",
  "maxHorizonDays",
  "cancelDeadlineMinutes",
  "rescheduleDeadlineMinutes",
  "quarantineMinutes",
  "prepayPercent",
  "noShowPolicy",
  "noShowThreshold",
  "maxActiveAppointments",
  "holdTtlSeconds",
  "waitlistHoldSeconds",
  "allowOnlineCancel",
  "allowOnlineReschedule",
]

export interface BranchDifference {
  readonly branchId: string
  readonly branchName: string
  readonly count: number
}

/** «Филиал на Гагарина отличается · 2» — сколько правил филиала расходятся с салонными. */
export function branchDifferences(settings: BookingRulesSettings): readonly BranchDifference[] {
  return settings.branches
    .map((branch) => ({
      branchId: branch.branchId ?? "",
      branchName: branch.branchName ?? "Филиал",
      count: COMPARED.filter((field) => branch[field] !== settings.salon[field]).length,
    }))
    .filter((difference) => difference.count > 0)
}

/** Правки одного экрана: только изменённые поля — иначе правка одного срока перепишет остальные. */
export type BookingRulesPatch = Partial<Omit<UpdateBookingRulesRequest, "branchId" | "expectedUpdatedAt">>

/** Что изменилось в форме по сравнению с загруженным — только это и отправляем. */
export function changedFields(before: BookingRulesEntry, after: BookingRulesEntry): BookingRulesPatch {
  const patch: Record<string, number | string | boolean> = {}
  for (const field of COMPARED) {
    if (before[field] !== after[field]) patch[field] = after[field] as number | string | boolean
  }
  return patch as BookingRulesPatch
}
